// LiveStreamUserAvatar.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Observer from '@researchgate/react-intersection-observer';

import StatefulImage from '../component/StatefulImage.jsx';
import LazyImage, { Image } from '../component/LazyImage.jsx';

import DecryptionWrapper from '../container/DecryptionWrapper.js';
import TextUserAvatar from '../container/TextUserAvatar.js';

import UserRankBadgeSrc1 from '../../img/img_leaderboard_list_no1.svg';
import UserRankBadgeSrc2 from '../../img/img_leaderboard_list_no2.svg';
import UserRankBadgeSrc3 from '../../img/img_leaderboard_list_no3.svg';
import DiamondIconSrc from '../../img/ic_diamond_xs.svg';
import PrivateShowMVPBorderSrc from '../../img/img_leaderboard_list_private_no1.svg';
import PrivateShowMVPRibbonSrc from '../../img/img_private_mvp.svg';
import { liveStreamChatType } from '../resource/liveStreamConstants.js';
import { getUserPicture, MediaAssetFormat } from '../resource/getMediaAsset.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';

export class LiveStreamUserAvatar extends React.PureComponent {
  state = {
    isIntersected: false,
  };
  queuePriority = Math.floor(Date.now() / 1000);
  nextTick = null;
  componentDidMount() {
    this.nextTick = setTimeout(() => this.fetchUser());
  }
  componentDidUpdate(prevProps, prevState) {
    const { userId, username } = this.props;
    const { isIntersected } = this.state;
    if (
      userId !== prevProps.userId ||
      username !== prevProps.username ||
      isIntersected !== prevState.isIntersected
    ) {
      clearTimeout(this.nextTick);
      this.nextTick = setTimeout(() => this.fetchUser());
    }
  }
  componentWillUnmount() {
    this.nextTick && clearTimeout(this.nextTick);
  }
  fetchUser = () => {
    const { userId, username, fetchUser } = this.props;
    const { isIntersected } = this.state;
    if (!isIntersected || !userId || username) {
      return;
    }
    return fetchUser({ userId });
  };
  renderMVPBadge = () => {
    const { badgeSize } = this.props;
    return (
      <BadgeImage
        src={PrivateShowMVPBorderSrc}
        badgeSize={badgeSize}
        alt="mvp badge"
      />
    );
  };
  renderRankBadge = () => {
    const { rank, defaultRank, badgeSize } = this.props;
    const badgeRank = rank || defaultRank;
    let badgeImage = null;
    switch (badgeRank) {
      case 1:
        badgeImage = UserRankBadgeSrc1;
        break;
      case 2:
        badgeImage = UserRankBadgeSrc2;
        break;
      case 3:
        badgeImage = UserRankBadgeSrc3;
        break;
      default:
        break;
    }

    if (!badgeImage) return null;
    return badgeImage ? (
      <BadgeImage
        src={badgeImage}
        badgeSize={badgeSize}
        alt={`badge image with rank ${badgeRank}`}
      />
    ) : null;
  };
  renderPlaceholder = () => {
    const { userId, avatarSize, fontSize } = this.props;
    return (
      <TextUserAvatar
        userId={userId}
        avatarSize={avatarSize}
        fontSize={fontSize}
      />
    );
  };
  renderAvatar = () => {
    const {
      userId,
      meId,
      queueDelayMsec,
      avatarSize,
      color,
      type,
      senderAvatarId,
      hasSenderAvatarId,
      borderWidth,
      borderColor,
    } = this.props;

    const shouldDisplayPlaceholder =
      !userId ||
      (type === liveStreamChatType.CHAT && !senderAvatarId) ||
      (hasSenderAvatarId !== undefined && !hasSenderAvatarId);

    if (shouldDisplayPlaceholder) {
      return (
        <Avatar
          color={color}
          size={avatarSize}
          borderWidth={borderWidth}
          borderColor={borderColor}
        >
          {this.renderPlaceholder()}
        </Avatar>
      );
    }

    const userPictureSource = getUserPicture({
      userId,
      meId,
      size: 64,
      format: MediaAssetFormat.WEBP,
    }).href;
    const [avifSrcSet, webpSrcSet, jpgSrcSet] = [
      MediaAssetFormat.AVIF,
      MediaAssetFormat.WEBP,
      MediaAssetFormat.JPG,
    ].map(format =>
      getPublicSrcSet({ href: userPictureSource, size: 64, format })
    );

    return (
      <Avatar
        color={color}
        size={avatarSize}
        borderWidth={borderWidth}
        borderColor={borderColor}
      >
        <StatefulImage>
          <Avatar
            data-key="lodaing"
            color={color}
            size={avatarSize}
            borderWidth={borderWidth}
            borderColor={borderColor}
          >
            {this.renderPlaceholder()}
          </Avatar>
          <Avatar
            data-key="error"
            color={color}
            size={avatarSize}
            borderWidth={borderWidth}
            borderColor={borderColor}
          >
            {this.renderPlaceholder()}
          </Avatar>
          <LazyImage
            data-key="target"
            threshold={0}
            queueConfig={{
              queueId: 'LiveStreamUserAvatar',
              queueDelayMsec,
              pQueueOptions: { concurrency: 2 },
              taskOptions: { priority: this.queuePriority },
            }}
            width={avatarSize}
            height={avatarSize}
          >
            <DecryptionWrapper resourceUrl={jpgSrcSet.src}>
              <source srcSet={avifSrcSet.srcSet} type="image/avif" />
              <source srcSet={webpSrcSet.srcSet} type="image/webp" />
              <Image {...jpgSrcSet} />
            </DecryptionWrapper>
          </LazyImage>
        </StatefulImage>
      </Avatar>
    );
  };
  renderDiamondIcon = () => {
    const { rank, diamondSize } = this.props;
    if (rank && rank <= 3) {
      return (
        <DiamondIcon
          src={DiamondIconSrc}
          diamondSize={diamondSize}
          alt="diamond"
        />
      );
    }
    return null;
  };
  renderMVPRibbon = () => {
    return <MVPRibbon src={PrivateShowMVPRibbonSrc} alt="MVP ribbon" />;
  };
  render() {
    const {
      badgeSize,
      avatarSize,
      isMVP,
      userId,
      defaultMVP,
      shouldDisplayMVP,
    } = this.props;
    const shouldDisplayMVPStyle =
      isMVP || (shouldDisplayMVP && !userId && defaultMVP);

    return (
      <Observer
        onChange={({ isIntersecting }, unobserve) => {
          if (isIntersecting) {
            this.setState({ isIntersected: true });
            unobserve();
          }
        }}
      >
        <StyledLiveStreamUserAvatar size={badgeSize || avatarSize}>
          {shouldDisplayMVPStyle
            ? this.renderMVPBadge()
            : this.renderRankBadge()}
          {this.renderAvatar()}
          {shouldDisplayMVPStyle
            ? this.renderMVPRibbon()
            : this.renderDiamondIcon()}
        </StyledLiveStreamUserAvatar>
      </Observer>
    );
  }
}

LiveStreamUserAvatar.propTypes = {
  userId: PropTypes.string,
  meId: PropTypes.string,
  fetchUser: PropTypes.func,
  username: PropTypes.string,
  color: PropTypes.string,
  queueDelayMsec: PropTypes.number,
  badgeSize: PropTypes.number,
  avatarSize: PropTypes.number,
  fontSize: PropTypes.number,
  diamondSize: PropTypes.number,
  rank: PropTypes.number,
  defaultRank: PropTypes.number,
  isMVP: PropTypes.bool,
  defaultMVP: PropTypes.bool,
  shouldDisplayMVP: PropTypes.bool,
  type: PropTypes.string,
  senderAvatarId: PropTypes.string,
  hasSenderAvatarId: PropTypes.bool,
  borderWidth: PropTypes.number,
  borderColor: PropTypes.string,
};

LiveStreamUserAvatar.defaultProps = {
  userId: '',
  meId: '',
  fetchUser: () => null,
  username: '',
  color: '#191919',
  queueDelayMsec: 0,
  badgeSize: 36,
  avatarSize: 24,
  fontSize: 16,
  diamondSize: 12,
  rank: null,
  defaultRank: null,
  isMVP: null,
  defaultMVP: false,
  shouldDisplayMVP: true,
  type: null,
  senderAvatarId: null,
  hasSenderAvatarId: undefined,
  borderWidth: 0,
  borderColor: 'transparent',
};

const StyledLiveStreamUserAvatar = styled.div`
  position: relative;
  height: ${({ size }) => `${size}px`};
  width: ${({ size }) => `${size}px`};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Avatar = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  border-radius: 50%;
  height: ${({ size }) => `${size}px`};
  width: ${({ size }) => `${size}px`};
  min-width: ${({ size }) => `${size}px`};
  transform: translate(-50%, -50%);
  background-color: ${({ color }) => color};
  border-width: ${({ borderWidth }) => `${borderWidth}px`};
  border-color: ${({ borderColor }) => borderColor};
  border-style: ${({ borderWidth }) => (borderWidth > 0 ? 'solid' : 'none')};
  box-sizing: border-box;
  overflow: hidden;

  picture {
    height: ${({ height }) => `${height}px`};
    width: ${({ width }) => `${width}px`};
    min-width: ${({ width }) => `${width}px`};
    border-radius: 50%;
    overflow: hidden;
    object-fit: cover;
  }

  // for renderPlaceholder() when avatar border width > 0
  & > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const BadgeImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ badgeSize }) => `${badgeSize}px`};
  height: ${({ badgeSize }) => `${badgeSize}px`};
  z-index: 1;
`;

const DiamondIcon = styled.img`
  position: absolute;
  width: ${({ diamondSize }) => `${diamondSize}px`};
  height: ${({ diamondSize }) => `${diamondSize}px`};
  bottom: 0;
  z-index: 3;
`;

const MVPRibbon = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 2;
`;

export default LiveStreamUserAvatar;
